/**
 * Phone helpers — the frontend twin of soroby-api/src/utils/phone.ts.
 * Keep the two in step; phone.test.js is the spec for both.
 *
 * Built on libphonenumber-js so any country works, not just Romania. The
 * company's default country is loaded once per session (useCompanyMessaging
 * in the dashboard shell) and applied to any locally-typed number:
 *
 *   normalizePhone('0721 172 247')      -> '+40721172247'   (country RO)
 *   normalizePhone('+40 721 172 247')   -> '+40721172247'
 *   normalizePhone('0040721172247')     -> '+40721172247'
 *   normalizePhone('+381 64 123 4567')  -> '+381641234567'  (other country kept)
 *   normalizePhone('abc')               -> null
 *
 * A "+" or "00" prefix always wins over the company default, so a Serbian
 * number on a Romanian tenant is never rewritten.
 */
import {
    AsYouType,
    getCountries,
    getCountryCallingCode,
    parsePhoneNumberFromString,
} from 'libphonenumber-js';

export const DEFAULT_PHONE_COUNTRY = 'RO';
/** Legacy: Romania's dial code. Prefer DEFAULT_PHONE_COUNTRY. */
export const DEFAULT_PHONE_COUNTRY_CODE = '40';

const onlyDigits = (s) => String(s ?? '').replace(/\D/g, '');

// dial digits -> ISO code, built once. Shared dial codes (e.g. "1" for
// US/CA, "44" for GB/GG/JE) resolve to the country people usually mean.
let dialToIso = null;
const dialMap = () => {
    if (!dialToIso) {
        dialToIso = new Map();
        for (const iso of getCountries()) {
            const dial = getCountryCallingCode(iso);
            if (!dialToIso.has(dial)) dialToIso.set(dial, iso);
        }
        for (const [dial, iso] of [['1', 'US'], ['44', 'GB'], ['7', 'RU'], ['39', 'IT'], ['47', 'NO'], ['61', 'AU']]) {
            dialToIso.set(dial, iso);
        }
    }
    return dialToIso;
};

const isCountryCode = (v) => getCountries().includes(v);

/**
 * Accepts an ISO alpha-2 code ("RO", "ro") or legacy dial digits ("40", "+40")
 * and returns the ISO code; null when it is neither.
 */
export const resolvePhoneCountry = (country) => {
    const text = String(country ?? '').trim();
    if (!text) return null;
    const upper = text.toUpperCase();
    if (/^[A-Z]{2}$/.test(upper) && isCountryCode(upper)) return upper;
    const dial = onlyDigits(text);
    if (dial) return dialMap().get(dial) ?? null;
    return null;
};

let sessionCountry = DEFAULT_PHONE_COUNTRY;

/**
 * Called once the company's settings are known; every helper below uses it.
 * `country` (ISO, e.g. "RO") wins; `countryCode` (dial digits, e.g. "40") is
 * the legacy setting and is mapped to its country.
 */
export const configurePhoneDefaults = ({ country, countryCode } = {}) => {
    const iso = resolvePhoneCountry(country) || resolvePhoneCountry(countryCode);
    if (iso) sessionCountry = iso;
};

/** The session default country as ISO alpha-2 ("RO"). */
export const getPhoneCountry = () => sessionCountry;

/** The session default country's dial digits ("40") — kept for older callers. */
export const getPhoneCountryCode = () => getCountryCallingCode(sessionCountry);

/** Dial digits ("40") for an ISO code or legacy dial digits. */
export const dialCodeForCountry = (country) =>
    getCountryCallingCode(resolvePhoneCountry(country) || sessionCountry);

const parse = (raw, defaultCountry) => {
    const text = String(raw ?? '').trim();
    if (!text) return null;
    const country = resolvePhoneCountry(defaultCountry) || sessionCountry;
    // "00" is the international prefix in most countries; libphonenumber only
    // knows it for some default countries, so rewrite it to "+" ourselves.
    const input = /^\s*00\d/.test(text) ? `+${text.replace(/^\s*00/, '')}` : text;
    return parsePhoneNumberFromString(input, country) ?? null;
};

/**
 * E.164 ("+40721172247") for anything that parses as a possible number,
 * else null. `defaultCountry` may be ISO ("RO") or legacy dial digits ("40");
 * a "+"/"00" prefix in the number always wins.
 */
export const normalizePhone = (raw, defaultCountry = sessionCountry) => {
    const parsed = parse(raw, defaultCountry);
    if (!parsed) return null;
    if (!parsed.isPossible() && !parsed.isValid()) return null;
    return parsed.number;
};

/** True when the number is a valid number for its (typed or default) country. */
export const isValidPhone = (raw, defaultCountry = sessionCountry) => {
    const parsed = parse(raw, defaultCountry);
    return !!parsed && parsed.isValid();
};

/**
 * Human-friendly international form for display ("+40 721 172 247");
 * falls back to the raw text when it does not parse.
 */
export const formatPhoneDisplay = (raw, defaultCountry = sessionCountry) => {
    const parsed = parse(raw, defaultCountry);
    if (!parsed) return String(raw ?? '');
    return parsed.formatInternational();
};

/** Parsed pieces of a stored number, for editors that show country + local part. */
export const splitPhone = (raw, defaultCountry = sessionCountry) => {
    const parsed = parse(raw, defaultCountry);
    if (!parsed) return null;
    return {
        country: parsed.country || resolvePhoneCountry(defaultCountry) || sessionCountry,
        national: parsed.nationalNumber,
        nationalFormatted: parsed.formatNational(),
        e164: parsed.number,
        valid: parsed.isValid(),
    };
};

/** Formats a partially-typed national number for a country as the user types. */
export const formatAsYouType = (text, country) => new AsYouType(country).input(String(text ?? ''));

/** E.164 without the "+", as wa.me and viber:// want it; '' when there's no number. */
export const phoneDigitsForLink = (raw, defaultCountry) =>
    (normalizePhone(raw, defaultCountry) ?? '').replace(/^\+/, '');
